import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Trash2, Download, Loader2, UserRound } from "lucide-react";
import { toNum } from "@/lib/utils";
import {
  comboVisualCodigo,
  comboVisualNombre,
  comboVisualPrecioSum,
  groupComboVisualRows,
} from "@/lib/comboLine";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { toast } from "sonner";
import { clientesAPI, ventasAPI, type Cliente, isForbiddenError, isServerError } from "@/lib/api";
import { openPdfTab } from "@/lib/openPdfTab";
import { RemitoOperatorBadges } from "@/lib/RemitoOperatorBadges";
import { normalizeClienteTiposPermitidos } from "@/lib/clienteTipoFactura";
import { presupuestoClienteEditable } from "@/lib/presupuestoClienteEditable";
import { dataRefresh, useDataRefresh } from "@/lib/dataRefresh";
import { Pagination } from "@/components/ui/pagination";
import { PresupuestoCalendario } from "./PresupuestoCalendario";
import {
  etiquetaRango,
  interpretarFechaBusqueda,
  rangoUltimos30,
  type RangoFecha,
} from "./presupuestoBusqueda";
import { ClientesABM } from "@/modules/Clientes/ClientesABM";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


interface Presupuesto {
  id?: number;
  numero: string;
  fecha: string;
  cliente: {
    id?: number;
    nombre: string;
    codigo: string;
    cuit: string;
    dni?: string;
    tipoFacturaPermitida?: ("A" | "B" | "X")[];
  };
  total: number;
  estado: string;
  clienteEditable?: boolean;
  cliente_editable?: boolean;
  items: {
    codigo_producto?: string;
    codigoProducto?: string;
    nombre_producto?: string;
    nombreProducto?: string;
    precio_unitario?: number;
    precioUnitario?: number;
    cantidad: number;
    descuento1: number;
    descuento2: number;
    combo_group?: string | null;
    comboGroup?: string | null;
  }[];
  usuarioNombre?: string | null;
  usuario_nombre?: string | null;
}

interface PresupuestosListadoProps {
  presupuestos: Presupuesto[];
  onPresupuestoDeleted?: (numero: string) => void;
}

export function PresupuestosListado({ presupuestos: _presupuestos, onPresupuestoDeleted }: PresupuestosListadoProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [presupuestoToDelete, setPresupuestoToDelete] = useState<string | null>(null);
  const [downloadingPDF, setDownloadingPDF] = useState<number | null>(null);
  const [presupuestoToReassign, setPresupuestoToReassign] = useState<Presupuesto | null>(null);
  const [reassignQuery, setReassignQuery] = useState("");
  const [reassignFound, setReassignFound] = useState<Cliente[]>([]);
  const [reassignError, setReassignError] = useState<string | null>(null);
  const [showNuevoClienteReassign, setShowNuevoClienteReassign] = useState(false);
  const [savingReassign, setSavingReassign] = useState(false);
  const keepReassignTargetRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [rango, setRango] = useState<RangoFecha>(() => rangoUltimos30());
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<Presupuesto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const fecha = interpretarFechaBusqueda(debouncedQuery);
      const params: Record<string, string | number> = {
        page,
        page_size: 25,
        fecha__gte: fecha?.desde ?? rango.desde,
        fecha__lte: fecha?.hasta ?? rango.hasta,
      };
      if (!fecha && debouncedQuery) params.search = debouncedQuery;
      const response = await ventasAPI.listPresupuestosPage(params);
      setRows(response.results || []);
      setTotal(response.count || 0);
    } catch {
      setRows([]);
      setTotal(0);
      toast.error("No se pudieron cargar los presupuestos");
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, page, rango]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useDataRefresh(["presupuestos"], cargar);

  useEffect(() => {
    if (!presupuestoToReassign) {
      setReassignFound([]);
      setReassignError(null);
      return;
    }
    const q = reassignQuery.trim();
    if (!q) {
      setReassignFound([]);
      setReassignError(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const results = await clientesAPI.lookup({ search: q, page_size: 10 });
        setReassignFound((results || []).filter((c) => c.codigo !== "CF"));
        setReassignError(null);
      } catch (error) {
        setReassignFound([]);
        if (isForbiddenError(error)) setReassignError("Sin permiso para buscar clientes.");
        else if (isServerError(error)) setReassignError("Error del servidor al buscar clientes.");
        else setReassignError("No se pudieron buscar clientes.");
      }
    }, 300);
    return () => clearTimeout(t);
  }, [reassignQuery, presupuestoToReassign]);

  const applyClienteToPresupuesto = async (cliente: Cliente) => {
    const p = presupuestoToReassign;
    if (!p?.id || !cliente.id) return;
    try {
      setSavingReassign(true);
      await ventasAPI.updatePresupuesto(p.id, { cliente: cliente.id } as Partial<typeof p>);
      toast.success(`Presupuesto ${p.numero} reasignado a ${cliente.nombre}`);
      setPresupuestoToReassign(null);
      setReassignQuery("");
      setShowNuevoClienteReassign(false);
      dataRefresh.emit("presupuestos");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo cambiar el cliente del presupuesto");
    } finally {
      setSavingReassign(false);
    }
  };

  const handleDownloadPresupuestoPDF = async (presupuesto: Presupuesto) => {
    const id = presupuesto.id;
    if (!id) {
      toast.error("No se puede descargar: presupuesto sin ID");
      return;
    }
    try {
      setDownloadingPDF(id);
      const blob = await ventasAPI.downloadPresupuestoPDF(id);
      if (!blob) {
        toast.error("No se pudo generar el PDF del presupuesto");
        return;
      }
      const win = await openPdfTab(blob, undefined, { path: `/api/presupuestos/${id}/pdf/` });
      if (!win) {
        toast.error("Pop-up bloqueado. Habilitá pop-ups para este sitio.");
      } else {
        toast.success("PDF de presupuesto abierto en nueva pestaña");
      }
    } catch (error) {
      console.error("Error downloading presupuesto PDF:", error);
      toast.error("Error al descargar PDF del presupuesto");
    } finally {
      setDownloadingPDF(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const toggleRow = (numero: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(numero)) {
      newExpanded.delete(numero);
    } else {
      newExpanded.add(numero);
    }
    setExpandedRows(newExpanded);
  };

  const handleDeleteConfirm = async () => {
    const numero = presupuestoToDelete;
    setPresupuestoToDelete(null);
    if (!numero) return;
    const row = rows.find((p) => p.numero === numero);
    if (!row?.id) {
      toast.error("No se pudo eliminar el presupuesto");
      return;
    }
    try {
      await ventasAPI.deletePresupuesto(row.id);
      onPresupuestoDeleted?.(numero);
      toast.success("Presupuesto eliminado");
      void cargar();
    } catch {
      toast.error("No se pudo eliminar el presupuesto");
    }
  };

  const calcularTotalItem = (item: any) => {
    const precio = toNum(item.precioUnitario || item.precio_unitario);
    const cant = toNum(item.cantidad);
    const desc1 = toNum(item.descuento1);
    const desc2 = toNum(item.descuento2);

    let total = precio * cant;
    if (desc1 > 0) total -= total * (desc1 / 100);
    if (desc2 > 0) total -= total * (desc2 / 100);
    return total;
  };

  const fechaEscrita = interpretarFechaBusqueda(debouncedQuery);
  const rangoVisible = fechaEscrita ?? rango;

  return (
    <>
      <div className="mb-4 flex items-center gap-2 max-w-xl">
        <SearchInput
          placeholder="Buscar número, cliente o fecha (23/10/2026)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <PresupuestoCalendario
          onChange={(siguiente) => {
            setRango(siguiente);
            setPage(1);
          }}
          onClear={() => {
            setRango(rangoUltimos30());
            setPage(1);
          }}
          clearLabel="Últimos 30 días"
        />
      </div>
      <div className="mb-3 text-xs text-muted-foreground">
        {etiquetaRango(rangoVisible.desde, rangoVisible.hasta)}
        {loading ? " · cargando…" : ""}
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No se encontraron resultados
        </div>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="w-8"></th>
              <th className="text-left py-3 px-4 font-medium text-sm">Nro Presupuesto</th>
              <th className="text-left py-3 px-4 font-medium text-sm">Cliente</th>
              <th className="text-left py-3 px-4 font-medium text-sm">CUIT/DNI</th>
              <th className="text-left py-3 px-4 font-medium text-sm">Fecha</th>
              <th className="text-right py-3 px-4 font-medium text-sm">Total</th>
              <th className="text-left py-3 px-4 font-medium text-sm">Estado</th>
              <th className="text-center py-3 px-4 font-medium text-sm">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((presupuesto) => {
              // Determine which ID to show based on factura type
              const tiposP = normalizeClienteTiposPermitidos(
                presupuesto.cliente.tipoFacturaPermitida as string | string[] | undefined
              );
              const showDni = tiposP.includes("B") && !tiposP.includes("A");
              const idValue = showDni ? (presupuesto.cliente.dni || presupuesto.cliente.cuit) : presupuesto.cliente.cuit;
              const idLabel = showDni ? "DNI" : "CUIT";

              return (
                <React.Fragment key={presupuesto.numero}>
                  <tr className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-2">
                      <button
                        onClick={() => toggleRow(presupuesto.numero)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {expandedRows.has(presupuesto.numero) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div>{presupuesto.numero}</div>
                      <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1">
                        <RemitoOperatorBadges source={presupuesto} showGeneroLabel={false} />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex items-center gap-1">
                        <span>{presupuesto.cliente.nombre}</span>
                        {onPresupuestoDeleted && presupuestoClienteEditable(presupuesto) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            title="Cambiar cliente"
                            onClick={() => {
                              setPresupuestoToReassign(presupuesto);
                              setReassignQuery("");
                            }}
                          >
                            <UserRound className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex flex-col">
                        <span>{idValue}</span>
                        <span className="text-[10px] text-muted-foreground">{idLabel}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {formatDate(presupuesto.fecha)}
                    </td>
                    <td className="py-3 px-4 text-sm text-right">
                      ${Number(presupuesto.total).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${presupuesto.estado?.toLowerCase() === "pendiente"
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                          : (presupuesto.estado?.toLowerCase() === "finalizado" || presupuesto.estado?.toLowerCase() === "utilizado")
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                        {presupuesto.estado ? (presupuesto.estado.charAt(0).toUpperCase() + presupuesto.estado.slice(1).toLowerCase()) : ''}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={downloadingPDF === presupuesto.id}
                          onClick={() => handleDownloadPresupuestoPDF(presupuesto)}
                          title="Descargar PDF"
                          className="h-8 px-2 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 active:scale-95 transition-all dark:hover:bg-blue-950 dark:hover:text-blue-300 dark:hover:border-blue-700"
                        >
                          {downloadingPDF === presupuesto.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                        {presupuesto.estado !== "Utilizado" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPresupuestoToDelete(presupuesto.numero)}
                            className={`h-8 w-8 p-0 ${deleteIconButtonClass}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedRows.has(presupuesto.numero) && (
                    <tr>
                      <td colSpan={8} className="bg-muted/30 p-4">
                        <div className="space-y-2">
                          <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2">
                            <h4 className="font-semibold text-sm">Productos:</h4>
                            <RemitoOperatorBadges source={presupuesto} showGeneroLabel={false} />
                          </div>
                          <div className="border border-border rounded-lg overflow-x-auto">
                            <table className="w-full min-w-[640px]">
                              <thead className="bg-muted">
                                <tr>
                                  <th className="text-left py-2 px-4 text-xs font-medium">Código</th>
                                  <th className="text-left py-2 px-4 text-xs font-medium">Producto</th>
                                  <th className="text-center py-2 px-4 text-xs font-medium">Cant.</th>
                                  <th className="text-center py-2 px-4 text-xs font-medium">Desc.</th>
                                  <th className="text-center py-2 px-4 text-xs font-medium">Desc.</th>
                                  <th className="text-right py-2 px-4 text-xs font-medium">Precio Lista</th>
                                  <th className="text-right py-2 px-4 text-xs font-medium">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {presupuesto.items && presupuesto.items.length > 0 ? (
                                  groupComboVisualRows(presupuesto.items).map((row) => {
                                    const item = row.legs[0];
                                    const codigo = row.isCombo
                                      ? comboVisualCodigo(row.legs)
                                      : (item.codigoProducto || item.codigo_producto);
                                    const nombre = row.isCombo
                                      ? comboVisualNombre(row.legs)
                                      : (item.nombreProducto || item.nombre_producto);
                                    const precio = row.isCombo
                                      ? comboVisualPrecioSum(row.legs)
                                      : toNum(item.precioUnitario || item.precio_unitario);
                                    const total = row.legs.reduce((s, it) => s + calcularTotalItem(it), 0);
                                    return (
                                    <tr key={row.key} className="border-t border-border">
                                      <td className="py-2 px-4 text-xs whitespace-pre-line">{codigo}</td>
                                      <td className="py-2 px-4 text-xs">
                                        <span className="block max-w-[22rem] truncate" title={nombre}>{nombre}</span>
                                      </td>
                                      <td className="py-2 px-4 text-xs text-center">{item.cantidad}</td>
                                      <td className="py-2 px-4 text-xs text-center">{toNum(item.descuento1)}%</td>
                                      <td className="py-2 px-4 text-xs text-center">{toNum(item.descuento2)}%</td>
                                      <td className="py-2 px-4 text-xs text-right">
                                        ${precio.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </td>
                                      <td className="py-2 px-4 text-xs text-right font-medium">
                                        ${total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </td>
                                    </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan={7} className="py-4 text-center text-muted-foreground text-xs">
                                      No hay productos en este presupuesto
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      <Pagination
        currentPage={page}
        totalCount={total}
        pageSize={25}
        onPageChange={setPage}
      />

      <AlertDialog open={!!presupuestoToDelete} onOpenChange={() => setPresupuestoToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar presupuesto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El presupuesto {presupuestoToDelete} será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDeleteConfirm}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={!!presupuestoToReassign && !showNuevoClienteReassign}
        onOpenChange={(open) => {
          if (!open) {
            if (keepReassignTargetRef.current) {
              keepReassignTargetRef.current = false;
              return;
            }
            setPresupuestoToReassign(null);
            setReassignQuery("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Cambiar cliente del presupuesto</DialogTitle>
            <DialogDescription>
              {presupuestoToReassign
                ? `Reasignar ${presupuestoToReassign.numero}. Los ítems y combos se mantienen.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <SearchInput
                value={reassignQuery}
                onChange={(e) => setReassignQuery(e.target.value)}
                placeholder="Buscar cliente por nombre o código..."
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-10 whitespace-nowrap"
                onClick={() => {
                  keepReassignTargetRef.current = true;
                  setShowNuevoClienteReassign(true);
                }}
              >
                + Cliente
              </Button>
            </div>
            {savingReassign ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Guardando…
              </div>
            ) : reassignError ? (
              <div className="text-sm text-muted-foreground">{reassignError}</div>
            ) : (
              <div className="max-h-60 overflow-y-auto border rounded-lg">
                {reassignFound.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full px-4 py-2 text-left hover:bg-muted text-sm flex justify-between"
                    onClick={() => applyClienteToPresupuesto(c)}
                  >
                    <span>{c.nombre}</span>
                    <span className="text-xs text-muted-foreground">{c.codigo}</span>
                  </button>
                ))}
                {reassignQuery.trim() && reassignFound.length === 0 && (
                  <div className="px-4 py-2 text-sm text-muted-foreground">No se encontraron clientes</div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showNuevoClienteReassign} onOpenChange={setShowNuevoClienteReassign}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo cliente</DialogTitle>
            <DialogDescription>
              Alta con el ABM de Clientes. Después se reasigna a este presupuesto.
            </DialogDescription>
          </DialogHeader>
          {showNuevoClienteReassign && (
            <ClientesABM
              createOnly
              onClienteSaved={(cliente) => {
                setShowNuevoClienteReassign(false);
                void applyClienteToPresupuesto(cliente);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}